import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import usePageMeta from "@/hooks/usePageMeta";

const Card = ({ title, children, testid }) => (
  <div className="flex min-h-[70vh] items-center justify-center px-6 py-16" data-testid={testid}>
    <div className="w-full max-w-sm">
      <p className="eyebrow">Espace sécurisé</p>
      <h1 className="mt-2 font-display text-3xl font-semibold">{title}</h1>
      <div className="mt-6">{children}</div>
    </div>
  </div>
);

const Submit = ({ busy, children, testid }) => (
  <button type="submit" disabled={busy} data-testid={testid} className="flex h-12 w-full items-center justify-center gap-2 rounded-md bg-[#0F2840] text-sm font-bold text-white hover:bg-[#1a3a5c] disabled:opacity-60">
    {busy && <Loader2 className="h-4 w-4 animate-spin" />}{children}
  </button>
);

export function ForgotPassword() {
  usePageMeta("Mot de passe oublié");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title="Créer ou réinitialiser mon mot de passe" testid="forgot-page">
      {sent ? (
        <p className="flex gap-2 text-sm text-slate-700" data-testid="forgot-sent"><CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />Si un compte existe pour cette adresse, un lien personnel (valable 1 heure) vient de vous être envoyé par e-mail.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <div className="space-y-1.5"><Label htmlFor="fe">Adresse e-mail du compte</Label>
            <Input id="fe" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12" data-testid="forgot-email-input" /></div>
          <Submit busy={busy} testid="forgot-submit-btn">Recevoir le lien par e-mail</Submit>
        </form>
      )}
      <p className="mt-6 text-xs"><Link to="/connexion" className="underline">Retour à la connexion</Link></p>
    </Card>
  );
}

export function ResetPassword() {
  usePageMeta("Nouveau mot de passe");
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 10) return toast.error("10 caractères minimum.");
    if (pwd !== pwd2) return toast.error("Les deux mots de passe ne correspondent pas.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token: params.get("token") || "", password: pwd });
      toast.success("Mot de passe enregistré. Vous pouvez vous connecter.");
      nav("/connexion", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title="Choisir mon mot de passe" testid="reset-page">
      {!params.get("token") ? (
        <p className="text-sm text-slate-700" data-testid="reset-invalid">Ce lien est incomplet ou invalide. <Link to="/mot-de-passe-oublie" className="font-semibold underline">Demander un nouveau lien</Link></p>
      ) : (
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <div className="space-y-1.5"><Label htmlFor="p1">Nouveau mot de passe (10 caractères minimum)</Label>
          <Input id="p1" type="password" autoComplete="new-password" required value={pwd} onChange={(e) => setPwd(e.target.value)} className="h-12" data-testid="reset-password-input" /></div>
        <div className="space-y-1.5"><Label htmlFor="p2">Confirmer le mot de passe</Label>
          <Input id="p2" type="password" autoComplete="new-password" required value={pwd2} onChange={(e) => setPwd2(e.target.value)} className="h-12" data-testid="reset-password-confirm-input" /></div>
        <Submit busy={busy} testid="reset-submit-btn">Enregistrer mon mot de passe</Submit>
      </form>
      )}
    </Card>
  );
}
